import { Reorder } from "framer-motion";
import { GripVertical } from "lucide-react";
import { Group, ImageField, NodeField, SaveBar, getPath } from "@/admin/fields";

const sectionLabels = {
  hero: "Hero",
  stats: "Stats Bar",
  whatwedo: "What We Do",
  whychoose: "Why Choose Us",
};

const HomeTab = ({ content, update, saving, dirty, message, onSave }) => {
  const order = getPath(content, ["home", "sectionOrder"]);

  return (
    <div data-testid="admin-tab-content-home">
      <SaveBar tabId="home" saving={saving} dirty={dirty} message={message} onSave={onSave} />
      <Group title="Section Order (drag to reorder)" open>
        <p className="af-hint-block">
          Drag sections into the order you want them to appear on the homepage.
          The contact form and contact bar always stay at the bottom.
        </p>
        <Reorder.Group
          axis="y"
          values={order}
          onReorder={(value) => update(["home", "sectionOrder"], value)}
          className="af-reorder"
          data-testid="admin-section-order"
        >
          {order.map((id) => (
            <Reorder.Item
              key={id}
              value={id}
              className="af-reorder-item"
              data-testid={`admin-reorder-${id}`}
            >
              <GripVertical size={14} />
              {sectionLabels[id] || id}
            </Reorder.Item>
          ))}
        </Reorder.Group>
      </Group>

      <Group title="Hero">
        <ImageField
          label="Hero background image"
          value={getPath(content, ["home", "hero", "image"])}
          onChange={(value) => update(["home", "hero", "image"], value)}
          testId="admin-hero-image-field"
        />
        <NodeField content={content} update={update} label="Title line 1" path={["home", "hero", "title1"]} />
        <div className="af-grid-2">
          <NodeField content={content} update={update} label="Title line 2" path={["home", "hero", "title2"]} />
          <NodeField content={content} update={update} label="Title accent (green)" path={["home", "hero", "titleAccent"]} />
        </div>
        <NodeField content={content} update={update} label="Paragraph" path={["home", "hero", "paragraph"]} multiline />
        {getPath(content, ["home", "hero", "cards"]).map((card, index) => (
          <div className="af-grid-2" key={index}>
            <NodeField content={content} update={update} label={`Card ${index + 1} title`} path={["home", "hero", "cards", index, "title"]} />
            <NodeField content={content} update={update} label={`Card ${index + 1} subtitle`} path={["home", "hero", "cards", index, "sub"]} />
          </div>
        ))}
        <div className="af-grid-2">
          <NodeField content={content} update={update} label="Screen status small text" path={["home", "hero", "screen", "statusSmall"]} />
          <NodeField content={content} update={update} label="Screen status strong text" path={["home", "hero", "screen", "statusStrong"]} />
        </div>
        <div className="af-grid-3">
          {getPath(content, ["home", "hero", "screen", "rows"]).map((row, index) => (
            <NodeField content={content} update={update} key={index} label={`Screen row ${index + 1}`} path={["home", "hero", "screen", "rows", index]} />
          ))}
        </div>
      </Group>

      <Group title="Stats Bar">
        {getPath(content, ["home", "stats", "items"]).map((item, index) => (
          <div className="af-grid-3" key={index}>
            <NodeField content={content} update={update} label={`Stat ${index + 1} value`} path={["home", "stats", "items", index, "value"]} />
            <NodeField content={content} update={update} label="Label" path={["home", "stats", "items", index, "label"]} />
            <NodeField content={content} update={update} label="Subtext" path={["home", "stats", "items", index, "sub"]} />
          </div>
        ))}
      </Group>

      <Group title="What We Do">
        <NodeField content={content} update={update} label="Section title" path={["home", "whatwedo", "title1"]} multiline />
        <NodeField content={content} update={update} label="Title accent (green)" path={["home", "whatwedo", "accent"]} />
        <NodeField content={content} update={update} label="Paragraph" path={["home", "whatwedo", "paragraph"]} multiline />
        <NodeField content={content} update={update} label="Learn more label" path={["home", "whatwedo", "learnMoreLabel"]} />
        {getPath(content, ["home", "whatwedo", "cards"]).map((card, index) => (
          <Group title={`Card ${index + 1}: ${getPath(content, ["home", "whatwedo", "cards", index, "title", "text"]) || card.slug}`} key={card.slug || index}>
            <NodeField content={content} update={update} label="Title" path={["home", "whatwedo", "cards", index, "title"]} multiline />
            <NodeField content={content} update={update} label="Body" path={["home", "whatwedo", "cards", index, "body"]} multiline />
            <p className="af-hint-block">
              This card links to the matching umbrella section on the Services
              page. Edit the services themselves in the Services tab.
            </p>
          </Group>
        ))}
      </Group>

      <Group title="Why Choose Us">
        <ImageField
          label="Background image"
          value={getPath(content, ["home", "whychoose", "image"])}
          onChange={(value) => update(["home", "whychoose", "image"], value)}
          testId="admin-whychoose-image-field"
        />
        <div className="af-grid-2">
          <NodeField content={content} update={update} label="Title line 1" path={["home", "whychoose", "title1"]} />
          <NodeField content={content} update={update} label="Title line 2" path={["home", "whychoose", "title2"]} />
        </div>
        <NodeField content={content} update={update} label="Title accent (green)" path={["home", "whychoose", "accent"]} />
        <NodeField content={content} update={update} label="Paragraph" path={["home", "whychoose", "paragraph"]} multiline />
        {getPath(content, ["home", "whychoose", "features"]).map((feature, index) => (
          <NodeField content={content} update={update} key={index} label={`Feature ${index + 1} title`} path={["home", "whychoose", "features", index, "title"]} />
        ))}
      </Group>

    </div>
  );
};

export default HomeTab;
